<div class="flex items-center w-full">
  <button
    type="button"
    class="campaign-details-date-filter-trigger"
    [class.!hidden]="loading()"
    [matMenuTriggerFor]="dateFilterMenu"
    (menuOpened)="onMenuOpened()"
  >
    <div class="flex items-center justify-between gap-2 min-w-0 w-full">
      @let displayedDate =
        selectedRange() === EDateRangeType.custom
          ? form.get('endDate')?.value
            ? (form.get('startDate')?.value | date: 'dd.MM.yyyy') +
              ' - ' +
              (form.get('endDate')?.value | date: 'dd.MM.yyyy')
            : (form.get('startDate')?.value | date: 'dd.MM.yyyy')
          : (translationKeyPrefix() + '.' + selectedRange() | translate);

      <span
        class="line-clamp-1 text-ellipsis text-sm"
        matTooltip
        matTooltipPosition="above"
        [appTextClampTooltip]="displayedDate"
      >
        {{ displayedDate }}
      </span>
      <app-icon class="flex-shrink-0" src="icons/chevron-down.svg" [size]="18" />
    </div>
  </button>

  <mat-menu #dateFilterMenu="matMenu" (closed)="onMenuClosed()">
    <!-- Predefined Time Ranges -->
    <div class="px-1">
      <div class="text-xs text-shikenso-purple-base-60 mb-1 px-2">
        {{ translationKeyPrefix() + '.timeRange' | translate }}
      </div>

      @for (range of predefinedRanges; track range) {
        @if (visiblePredefinedRanges().has(range)) {
          <button
            type="button"
            mat-menu-item
            class="!min-h-8 !h-8 !px-2"
            (click)="onPredefinedRangeSelect(range, $event)"
          >
            <div class="flex items-center justify-between w-full gap-9">
              <span>{{ translationKeyPrefix() + '.' + range | translate }}</span>

              <app-icon
                class="w-4 h-4"
                [src]="selectedRange() === range ? 'icons/circle-check.svg' : 'icons/circle.svg'"
                [color]="selectedRange() === range ? 'var(--shikenso-pink-base)' : 'var(--shikenso-purple-base)'"
              />
            </div>
          </button>
        }
      }
    </div>

    <hr class="border-shikenso-purple-base-12 my-3 mx-1" />

    <!-- Custom Range -->
    <div class="px-1">
      <div class="text-xs text-shikenso-purple-base-60 mb-1 px-2">
        {{ translationKeyPrefix() + '.customRange' | translate }}
      </div>

      <div class="flex flex-col gap-1 px-2 custom-form campaign-details-date-filter-custom-range" [formGroup]="form">
        <!-- Start Date Picker -->
        <div class="flex min-w-[180px] cursor-pointer" (click)="openStartPicker($event)">
          <mat-form-field
            class="flex-1 [&_input]:pointer-events-none mat-mdc-form-field-type-mat-date-picker"
            [class.active-datepicker]="startRangePicker?.opened"
          >
            <input
              readonly
              matInput
              [value]="form.get('startDate')?.value | date: 'dd.MM.yyyy'"
              [placeholder]="'pages.campaignForm.sections.basics.startDatePlaceholder' | translate"
            />

            <div matSuffix class="flex items-center gap-1">
              <app-icon src="icons/chevron-down.svg" [size]="20" />
            </div>
            <mat-date-range-picker #startRangePicker></mat-date-range-picker>

            <div class="opacity-0">
              <mat-date-range-input
                #startRangeInput="matDateRangeInput"
                [rangePicker]="startRangePicker"
                [min]="allowedMinDate()"
                [max]="allowedMaxDate()"
              >
                <input matStartDate formControlName="startDate" />
                <input matEndDate formControlName="endDate" />
              </mat-date-range-input>
            </div>
          </mat-form-field>
        </div>

        <!-- End Date Picker -->
        <div class="flex min-w-[180px] cursor-pointer" (click)="openEndPicker($event)">
          <mat-form-field
            class="flex-1 [&_input]:pointer-events-none mat-mdc-form-field-type-mat-date-picker"
            [class.active-datepicker]="endDatePicker?.opened"
          >
            <input
              readonly
              matInput
              class="max-h-[48px]"
              [value]="form.get('endDate')?.value | date: 'dd.MM.yyyy'"
              [placeholder]="'pages.campaignForm.sections.basics.endDatePlaceholder' | translate"
            />

            <div matSuffix class="flex items-center gap-1">
              <app-icon src="icons/chevron-down.svg" [size]="20" />
            </div>
            <mat-datepicker
              #endDatePicker
              [panelClass]="datePickerUtilities.endPickerPanelClass"
              [dateClass]="
                datePickerUtilities.endPickerDateClass(form.get('startDate')?.value, form.get('endDate')?.value)
              "
            ></mat-datepicker>
            <mat-error [control]="form.get('endDate')" />

            <div class="opacity-0">
              <input
                matInput
                [matDatepicker]="endDatePicker"
                [min]="form.get('startDate')?.value ?? null"
                [matDatepickerFilter]="endDateFilter"
                [value]="form.get('endDate')?.value"
                (dateInput)="onEndDateChange($event.value ?? null)"
              />
            </div>
          </mat-form-field>
        </div>
      </div>
    </div>

    @if (showCompare()) {
      <hr class="border-shikenso-purple-base-12 my-3 mx-1" />

      <!-- Compare -->
      <div class="px-1" [class.opacity-40]="compareDisabled()" [class.pointer-events-none]="compareDisabled()">
        <div class="text-xs text-shikenso-purple-base-60 mb-1 px-2">
          {{ translationKeyPrefix() + '.compare' | translate }}
        </div>

        @for (mode of compareRangeOptions; track mode) {
          <button
            type="button"
            mat-menu-item
            class="!min-h-8 !h-8 !px-2"
            [disabled]="compareDisabled()"
            (click)="onCompareRangeSelect(mode, $event)"
          >
            <div class="flex items-center justify-between w-full gap-9">
              <span>{{ translationKeyPrefix() + '.' + mode | translate }}</span>

              <app-icon
                class="w-4 h-4"
                [src]="compareRangeType() === mode ? 'icons/circle-check.svg' : 'icons/circle.svg'"
                [color]="compareRangeType() === mode ? 'var(--shikenso-pink-base)' : 'var(--shikenso-purple-base)'"
              />
            </div>
          </button>
        }

        <!-- Always shown (like the main range's own "Custom Range" section above), regardless
             of which compare mode is selected - `compareForm` reflects whatever range is
             currently active (e.g. the computed previous period), so this doubles as a read-only
             preview of the active compare range and, once "Custom" is selected, an editable one.
             The heading itself still needs to match the mode though - "Compare custom range"
             would mislabel a "Previous period"/"None" preview as an actual custom range. -->
        <div class="text-xs text-shikenso-purple-base-60 mb-1 mt-3 px-2">
          {{
            (compareRangeType() === ECompareRangeType.custom
              ? translationKeyPrefix() + '.compareCustomRange'
              : translationKeyPrefix() + '.compareRangePreview'
            ) | translate
          }}
        </div>

        <div
          class="flex flex-col gap-1 px-2 custom-form campaign-details-date-filter-custom-range"
          [class.opacity-40]="compareRangeType() !== ECompareRangeType.custom"
          [class.pointer-events-none]="compareRangeType() !== ECompareRangeType.custom"
          [formGroup]="compareForm"
        >
          <!-- Compare Start Date Picker -->
          <div class="flex min-w-[180px] cursor-pointer" (click)="openCompareStartPicker($event)">
            <mat-form-field
              class="flex-1 [&_input]:pointer-events-none mat-mdc-form-field-type-mat-date-picker"
              [class.active-datepicker]="compareStartRangePicker?.opened"
            >
              <input
                readonly
                matInput
                [value]="compareForm.get('startDate')?.value | date: 'dd.MM.yyyy'"
                [placeholder]="'pages.campaignForm.sections.basics.startDatePlaceholder' | translate"
              />

              <div matSuffix class="flex items-center gap-1">
                <app-icon src="icons/chevron-down.svg" [size]="20" />
              </div>
              <mat-date-range-picker #compareStartRangePicker></mat-date-range-picker>

              <div class="opacity-0">
                <mat-date-range-input
                  #compareStartRangeInput="matDateRangeInput"
                  [rangePicker]="compareStartRangePicker"
                  [min]="allowedMinDate()"
                  [max]="allowedMaxDate()"
                >
                  <input matStartDate formControlName="startDate" />
                  <input matEndDate formControlName="endDate" />
                </mat-date-range-input>
              </div>
            </mat-form-field>
          </div>

          <!-- Compare End Date Picker -->
          <div class="flex min-w-[180px] cursor-pointer" (click)="openCompareEndPicker($event)">
            <mat-form-field
              class="flex-1 [&_input]:pointer-events-none mat-mdc-form-field-type-mat-date-picker"
              [class.active-datepicker]="compareEndDatePicker?.opened"
            >
              <input
                readonly
                matInput
                class="max-h-[48px]"
                [value]="compareForm.get('endDate')?.value | date: 'dd.MM.yyyy'"
                [placeholder]="'pages.campaignForm.sections.basics.endDatePlaceholder' | translate"
              />

              <div matSuffix class="flex items-center gap-1">
                <app-icon src="icons/chevron-down.svg" [size]="20" />
              </div>
              <mat-datepicker
                #compareEndDatePicker
                [panelClass]="datePickerUtilities.endPickerPanelClass"
                [dateClass]="
                  datePickerUtilities.endPickerDateClass(
                    compareForm.get('startDate')?.value,
                    compareForm.get('endDate')?.value
                  )
                "
              ></mat-datepicker>
              <mat-error [control]="compareForm.get('endDate')" />

              <div class="opacity-0">
                <input
                  matInput
                  [matDatepicker]="compareEndDatePicker"
                  [min]="compareForm.get('startDate')?.value ?? null"
                  [matDatepickerFilter]="endDateFilter"
                  [value]="compareForm.get('endDate')?.value"
                  (dateInput)="onCompareEndDateChange($event.value ?? null)"
                />
              </div>
            </mat-form-field>
          </div>
        </div>
      </div>
    }
  </mat-menu>

  @if (loading()) {
    <ngx-skeleton-loader
      class="h-[34px] w-full"
      [theme]="{ height: '34px', width: '100%', 'border-radius': '100vw' }"
    />
  }
</div>
